/** Customer copy for transport-level failures the API never named. */
const transportCopy = (code: string): string | undefined => {
	const m = /^http_(\d{3})$/.exec(code)
	if (!m) return undefined
	const status = Number(m[1])
	if (status === 401 || status === 403) return 'Sign in with the wallet that owns this offer and try again. Nothing was sent.'
	if (status === 404) return 'That offer could not be found. Refresh and try again.'
	if (status === 408 || status === 429) return 'The offers service is busy. Wait a moment and try again. Nothing was sent.'
	if (status >= 500) return 'The offers service is temporarily unavailable. Nothing was sent. Try again in a minute.'
	return 'The request could not be completed. Refresh the page and try again. Nothing was sent.'
}

export class OfferError extends Error {
	code: string
	/** HTTP status the API answered with, when the error came from a call. */
	status?: number
	/** What the API actually said (its message/detail or raw body), for telemetry. */
	detail?: string
	/** The offer the call was about, when the path named one. */
	offerId?: string
	/** Shaped like an axios error so the shared `errorDetail` reads it. */
	response?: { status?: number; data?: { message?: string; detail?: string; code?: string } }
	constructor(
		code: string,
		message?: string,
		extra: { status?: number; detail?: string; offerId?: string } = {}
	) {
		super(message || OFFER_ERROR_COPY[code] || transportCopy(code) || code)
		this.name = 'OfferError'
		this.code = code
		this.status = extra.status
		this.detail = extra.detail
		this.offerId = extra.offerId
		if (extra.status || extra.detail)
			this.response = { status: extra.status, data: { code, message: extra.detail } }
	}
}

/**
 * Service-side refusals that are definitive: the API did not take the
 * request, nothing was broadcast, and a retry will not help right now. They
 * can arrive with a 5xx or 429 status, so the outcome must not be treated as
 * unknown the way a transport failure after broadcast is.
 */
export const OFFER_DEFINITIVE_REFUSALS = new Set(['offers_paused', 'rate_limited', 'too_many_open_offers'])

/** Codes the API answers with before it reserves anything or broadcasts. */
export const OFFER_PRE_BROADCAST_CODES = new Set([
	'transaction_preflight_rejected',
	'node_preflight_unavailable',
	...OFFER_DEFINITIVE_REFUSALS
])

/**
 * Accept and fill answers that come before the settlement claim is taken
 * (or after it was given back): lookups, the co-signer, template and witness
 * construction. Several are 5xx, but none means a sale may have reached the
 * network, so the seller is free to try again.
 */
export const OFFER_PRE_SETTLEMENT_CODES = new Set([
	...OFFER_DEFINITIVE_REFUSALS,
	'cosigner_unavailable',
	'escrow_lookup_failed',
	'eligibility_lookup_failed',
	'item_lookup_failed',
	'item_prevout_unavailable',
	'offer_incomplete',
	'bad_outpoint',
	'sighash_failed',
	'witness_failed',
	'template_failed',
	'escrow_mismatch',
	'psbt_failed',
	'build_busy'
])

export const OFFER_ERROR_COPY: Record<string, string> = {
	// Service state the API announces before doing anything.
	offers_paused: 'Offers are paused for maintenance. Cancelling and refunds still work.',
	rate_limited: 'Too many offer requests. Try again in a minute.',
	build_busy: 'The offers service is busy. Wait a moment and try again. Nothing was sent.',
	too_many_open_offers:
		'This wallet has too many open offers on this item or collection. Cancel one before placing another. Nothing was sent.',
	cosigner_unavailable: 'Offers are temporarily unavailable. Try again in a minute.',
	reconciliation_unavailable: 'The offer’s status could not be checked right now. Try again shortly.',
	store_failed: 'The offer could not be saved. Nothing was sent. Try again.',
	body_too_large: 'The request was too large for the offers service. Refresh and try again. Nothing was sent.',
	invalid_request_body:
		'The request the app sent could not be read by the offers service. Refresh the page and try again. Nothing was sent.',
	unsupported_media_type:
		'The request the app sent could not be read by the offers service. Refresh the page and try again. Nothing was sent.',
	method_not_allowed: 'The offers service did not accept that request. Refresh the page and try again. Nothing was sent.',
	not_found: 'That offer could not be found. Refresh and try again.',
	http_error: 'The request could not be completed. Refresh the page and try again. Nothing was sent.',
	unauthorized: 'Sign in with the wallet that owns this offer to do that.',

	// Wallet and key checks.
	invalid_buyer: 'Connect a Bitcoin mainnet wallet to make an offer.',
	buyer_key_mismatch:
		'The connected wallet’s keys do not match this offer. Reconnect the wallet that made it and try again.',
	payment_key_mismatch:
		'The connected wallet’s keys do not match this offer. Reconnect the wallet that made it and try again.',
	buyer_key_invalid: 'Your wallet’s public key could not be read. Reconnect your wallet and try again.',
	invalid_payment_wallet: 'Your payment wallet could not be read. Reconnect your wallet and try again.',
	invalid_address: 'That is not a Bitcoin mainnet address.',
	invalid_seller_address: 'Your wallet address could not be read. Reconnect your wallet and try again.',
	batch_wallet_changed: 'The connected wallet changed. Reopen the form to continue.',
	insufficient_funds: 'Not enough confirmed BTC in your payment wallet for this offer.',

	// Building an offer.
	invalid_scope: 'Unknown offer type.',
	invalid_slug: 'That collection could not be found.',
	collection_not_found: 'That collection could not be found.',
	collection_inactive: 'This collection is not open for offers right now. Nothing was sent.',
	collection_lookup_failed: 'The collection could not be checked right now. Nothing was sent. Try again shortly.',
	invalid_trait: 'Pick a trait and a value for a trait offer.',
	invalid_inscription_id: 'That inscription could not be found.',
	inscription_not_found: 'That inscription could not be found.',
	owner_unknown: 'The current owner of this item is unknown.',
	cannot_offer_on_own_item: 'You already own this item.',
	owner_address_unsupported: 'This item is held by an address offers cannot pay yet.',
	item_value_unknown: 'The item output could not be read.',
	price_out_of_range: 'Offers must be at least 1,000 sats.',
	fee_rate_out_of_range: 'Pick a fee rate between 1 and 500 sat/vB.',
	invalid_quantity:
		'Choose a whole-number quantity from 1 to 10. Individual item offers have a quantity of one.',
	message_too_long: 'Keep your message to 500 characters or fewer.',
	invalid_message: 'Your message contains unsupported control characters.',
	escrow_build_failed: 'The offer escrow could not be built. Nothing was sent. Try again.',
	offer_mint_layout_failed: 'The funding transaction could not be laid out. Nothing was sent. Try again.',
	fungible_offers_unavailable:
		'Offers are available for NFT collections first. Token collections (TAP, BRC-20, Runes, Alkanes) are coming later. No funds were sent.',
	token_offer_protocol_unsupported: 'Offers on this token are not supported yet. Nothing was sent.',

	// Client-side checks: the wallet never saw these transactions, or what it
	// signed never left the page.
	funding_template_invalid:
		'The funding transaction the offers service built does not match this offer. Nothing was signed.',
	funding_signature_invalid:
		'Your wallet signed the funding transaction in a way this offer cannot use. Your offer was not placed. The signed funding was not broadcast.',
	accept_template_invalid:
		'The transaction the offers service asked you to pre-sign does not match this offer. Your offer was not placed. The signed funding was not broadcast.',
	escrow_signature_invalid:
		'Your wallet signed the offer escrow outside this offer’s terms. Your offer was not placed. The signed funding was not broadcast.',
	offer_template_oversigns:
		'The offers service asked your wallet to sign more than this offer. Your wallet did not sign it.',
	offer_terms_invalid:
		'The amounts the offers service quoted do not match this offer. Nothing was signed.',
	offer_intent_mismatch: 'Offer authorization terms changed. Nothing was funded.',
	offer_item_moved: 'This item just moved. Review it again.',
	offer_funding_pending:
		'Your offer’s funding reached the network and is still being confirmed. Check your offers in a few minutes before placing another one with the same coins.',
	item_output_changed:
		'The item output changed since you reviewed this offer. Review the updated amount before accepting.',

	// Funding and activation.
	offer_not_found: 'This offer no longer exists.',
	offer_not_building: 'This offer was already submitted.',
	legacy_offer_refund_only: 'This older offer can only be refunded. Cancel it to get your BTC back.',
	invalid_funding_psbt: 'The wallet returned an unusable funding transaction.',
	funding_not_signed:
		'Your wallet did not sign the funding transaction. Nothing was sent; try again when the wallet is unlocked.',
	funding_missing_escrow_output: 'The signed funding transaction does not match the offer.',
	funding_mint_mismatch: 'The signed funding transaction does not match the offer.',
	funding_inputs_pending:
		'Your payment balance is still updating after the previous offer. Wait for it to refresh before placing the remaining offers.',
	funding_outpoint_already_claimed:
		'These coins already fund another offer. Wait for your balance to update, then try again.',
	funding_claim_unavailable:
		'The funding could not be reserved right now. Nothing was broadcast. Try again shortly.',
	funding_broadcast_rejected: 'The network rejected the funding transaction.',
	funding_broadcast_failed: 'Could not reach the network. Try again.',
	activation_unreachable:
		'Your offer was signed but the offers service could not be reached to submit it. Nothing was broadcast. Try again in a moment.',
	offer_activation_conflict: 'This offer was already activated.',
	reload_failed: 'The offer was submitted but could not be reloaded. Refresh your offers to see its status.',
	buyer_presignature_invalid: 'The wallet did not sign the offer escrow. Try again.',
	accept_template_mutated: 'The signed transaction does not match the offer.',
	item_prevout_unavailable:
		'The item’s Bitcoin output could not be verified. Nothing was submitted. Try again shortly.',
	item_changed: 'The item moved while you were signing. Start the offer again.',
	transaction_preflight_rejected:
		'Bitcoin rejected the transaction before broadcast. Refresh the offer and review the current network fee.',
	node_preflight_unavailable:
		'Bitcoin network checks are unavailable. Nothing new was submitted. Try again shortly.',
	attempt_store_failed:
		'The transaction could not be saved for recovery. Nothing new was submitted. Try again shortly.',
	offer_attempt_pending:
		'This offer has a pending transaction. Refresh its status before signing again.',

	// Accepting and filling.
	offer_not_active: 'This offer is no longer active.',
	offer_expired: 'This offer has expired.',
	offer_changed: 'This offer changed while it was being checked. Refresh it and try again.',
	offer_incomplete: 'This offer is missing its funding details. Nothing was submitted. Try again shortly.',
	offer_not_fillable: 'This offer cannot be filled.',
	escrow_unfunded: 'This offer’s funding is no longer in the mempool.',
	escrow_lookup_failed:
		'The offer’s funding could not be checked right now. Nothing was submitted. Try again shortly.',
	escrow_mismatch: 'This offer’s escrow could not be verified. Nothing was submitted.',
	escrow_witness_invalid: 'The sale could not be completed. Nothing was submitted.',
	eligibility_lookup_failed:
		'The item could not be checked against this offer right now. Nothing was submitted. Try again shortly.',
	item_lookup_failed: 'The item could not be checked right now. Nothing was submitted. Try again shortly.',
	item_moved: 'The item has moved since the offer was made.',
	item_not_eligible: 'That item does not match this offer.',
	bad_outpoint: 'The item’s output could not be read. Nothing was submitted. Try again shortly.',
	not_the_owner: 'Only the current owner can accept this offer.',
	not_the_buyer: 'Only the buyer can cancel this offer.',
	use_fill_for_collection_offer: 'Collection offers are filled from an item you own.',
	use_accept_for_item_offer: 'This offer is for a specific item; accept it from that item.',
	cannot_fill_own_offer: 'You cannot sell into your own offer.',
	invalid_psbt: 'The wallet returned a transaction that could not be read. Nothing was submitted.',
	seller_not_signed: 'The item input was not signed.',
	seller_sighash_all_required:
		'Your wallet must sign the whole sale. Nothing was submitted; try again with the default signing option.',
	seller_signature_invalid: 'The wallet signature did not verify.',
	fill_template_mutated: 'The signed transaction does not match the offer.',
	template_failed: 'The sale transaction could not be built. Nothing was submitted. Try again.',
	psbt_failed: 'The transaction could not be prepared. Nothing was submitted. Try again.',
	sighash_failed: 'The sale could not be prepared for signing. Nothing was submitted. Try again.',
	witness_failed: 'The sale could not be completed. Nothing was submitted. Try again.',
	signing_reservation_unavailable:
		'Another transaction for this offer is being prepared. Refresh its status and try again.',
	broadcast_rejected: 'The network rejected the transaction.',
	broadcast_failed: 'Could not reach the network. Try again.',

	// Declining.
	challenge_unavailable: 'The decline could not be prepared. Try again shortly.',
	invalid_action_challenge: 'The decline authorization expired. Reload this offer and try again.',

	// Cancelling and refunds.
	offer_not_cancellable: 'This offer cannot be cancelled in its current state.',
	offer_refund_pending: 'A refund for this offer is already pending. Refresh its status.',
	refund_build_failed: 'The refund could not be prepared. Try again shortly.',
	refund_template_mutated: 'The signed refund does not match the reviewed refund. Nothing was submitted.',
	refund_witness_invalid: 'The wallet did not sign the refund correctly. Nothing was submitted.',
	refund_below_dust: 'This fee would leave too little BTC to refund. Choose a lower fee.',
	refund_fee_too_high: 'That refund fee is higher than this offer allows. Choose a lower fee.',
	buyer_not_signed: 'Your wallet did not sign the refund. Nothing was submitted; try again when the wallet is unlocked.',
	buyer_signature_invalid: 'The wallet did not sign the refund. Try again.',

	// Wallet reads.
	invalid_txid: 'That transaction ID is not valid.',

	// Token (TAP / DMT-NAT) offers.
	nat_offers_unavailable:
		'NAT offers are awaiting verified TAP index integration. No funds were sent.',
	nat_terms_required: 'This NAT offer needs an exact token amount and total price.',
	invalid_nat_terms: 'The NAT amount or price changed. Review the offer again.',
	invalid_nat_scope: 'NAT offers are collection offers on the DMT-NAT collection.',
	nat_amount_precision: 'Enter a whole NAT amount.',
	nat_integer_fee_rate_required: 'NAT offers need a whole-number fee rate.',
	nat_decimals_unsupported: 'This token’s decimals are not supported for offers yet.',
	nat_deployment_invalid: 'The DMT-NAT deployment could not be verified. Nothing was sent.',
	nat_collection_amount_mismatch: 'This transfer does not hold the amount this offer needs.',
	nat_collection_evidence_missing: 'This transfer could not be verified in the collection index.',
	nat_collection_index_unavailable: 'The collection index is unavailable. Nothing was sent. Try again shortly.',
	nat_fresh_transfer_required: 'Use a fresh transfer that has never been signed for sale.',
	nat_transfer_consumed_or_wrong_amount:
		'Use a fresh, unconsumed transfer for exactly this NAT amount.',
	nat_prior_listing_authorization:
		'This transfer has a prior listing authorization. Use a fresh transfer that has never been listed.',
	nat_listing_check_unavailable: 'Prior listings could not be checked right now. Nothing was sent. Try again shortly.',
	nat_transfer_invalid: 'That is not a valid DMT-NAT transfer.',
	nat_transfer_unconfirmed: 'The transfer must confirm before it can fill an offer.',
	nat_transfer_chain_mismatch: 'The transfer is not on the chain the index follows. Try again shortly.',
	nat_ambiguous_output: 'The transfer shares its output with other assets. Use a transfer in its own output.',
	nat_output_spent: 'The transfer’s output was already spent.',
	nat_owner_or_delivery_mismatch: 'The transfer is not owned by your wallet or would not reach the buyer.',
	nat_delivery_not_indexed: 'The NAT delivery is not indexed yet. Check again shortly.',
	nat_delivery_unconfirmed: 'The NAT delivery has not confirmed yet. Check again shortly.',
	nat_index_stale: 'The TAP index is behind Bitcoin. Try again after it catches up.',
	nat_index_changed: 'The TAP index changed while the offer was checked. Try again.',
	nat_index_invalid: 'The TAP index returned data that could not be verified. Nothing was sent.',
	nat_index_unavailable: 'The TAP index is unavailable. Nothing was sent. Try again shortly.',
	nat_asset_index_stale: 'The token index is behind Bitcoin. Try again after it catches up.',
	nat_asset_index_unavailable: 'The token index is unavailable. Nothing was sent. Try again shortly.',
	nat_asset_index_fork_mismatch: 'The token index is on a different chain tip than Bitcoin. Try again shortly.',
	nat_chain_unavailable: 'Bitcoin could not be reached to check the transfer. Try again shortly.',
	invalid_transfer_query: 'That transfer search could not be read.',
	asset_index_unavailable: 'The token index is unavailable. Nothing was sent. Try again shortly.',
	asset_index_invalid: 'The token index returned data that could not be verified. Nothing was sent.',
	asset_index_disagrees: 'The token index disagrees with Bitcoin right now. Nothing was sent. Try again shortly.',
	asset_index_fork_mismatch: 'The token index is on a different chain tip than Bitcoin. Try again shortly.',
	asset_index_tip_changed: 'A new block arrived while the offer was checked. Try again.',
	chain_tip_unavailable: 'Bitcoin could not be reached right now. Nothing was sent. Try again shortly.'
}
